import React, { useRef, useState, forwardRef } from 'react';
import { Download, BookMarked } from 'lucide-react';
import { downloadNodeAsPdf } from '../../utils/manualPdf';

/**
 * One page of the manual.
 *
 * Visual contract — every page has the SAME outer structure so the book
 * looks consistent when flipping :
 *   - Header band  : 40 px (px-5 py-2, chapter-coloured)
 *   - Body         : flex-1 with px-7 py-6, scrolls if content overflows
 *   - Footer band  : 40 px (px-5 py-2, PDF button + copyright)
 *
 * IMPORTANT — Color tokens : the manual page renders on a fixed cream
 * "paper" background (#faf5e9) regardless of app theme. We MUST NOT use
 * Tailwind `text-slate-*` classes here because those are theme-aware in
 * this codebase (slate-900 = dark in dark mode, but LIGHT in light mode
 * since the slate scale is inverted via CSS vars). So we hardcode every
 * paper-side text colour to a frozen hex value (`text-[#0F172A]` etc.).
 * This guarantees AAA contrast (≥ 13 : 1) on cream regardless of theme.
 */
const ManualPage = forwardRef(function ManualPage({ chapter, page, chapterPageIndex }, ref) {
  const printRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const filename = `hst-${chapter.id}-${page.type}`;

  const handleDownload = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      await downloadNodeAsPdf(printRef.current, filename);
    } catch (err) {
      console.warn('[manual] PDF export failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      ref={ref}
      className="manual-page h-full w-full bg-[#faf5e9] text-[#0F172A] flex flex-col font-sans overflow-hidden"
    >
      {/* HEADER BAND — 40 px height, chapter colour, identical on every page */}
      <div
        className="px-5 h-10 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] font-bold text-white flex-shrink-0"
        style={{ background: chapter.color }}
      >
        <span className="truncate">{chapter.title}</span>
        <span className="opacity-90 ml-2 flex-shrink-0">
          {page.type === 'cover' ? 'Intro' : `Page ${chapterPageIndex + 1}`}
        </span>
      </div>

      {/* PRINT AREA — single padding for ALL pages so they look uniform.
          overflow-y-auto = safety net if content still overflows. */}
      <div
        ref={printRef}
        className="flex-1 px-6 py-5 overflow-y-auto bg-[#faf5e9]"
        style={{ minHeight: 0 }}
      >
        {page.type === 'cover' && <CoverContent chapter={chapter} page={page} />}
        {page.type === 'definitions' && <DefinitionsContent page={page} chapter={chapter} />}
        {page.type === 'formulas' && <FormulasContent page={page} chapter={chapter} />}
        {page.type === 'vocabulary' && <VocabularyContent page={page} chapter={chapter} />}
        {page.type === 'cases' && <CasesContent page={page} chapter={chapter} />}
        {page.type === 'pitfalls' && <PitfallsContent page={page} chapter={chapter} />}
        {page.type === 'diagram' && <DiagramContent page={page} chapter={chapter} />}
      </div>

      {/* FOOTER BAND — 40 px height, identical on every non-cover page */}
      <div className="px-5 h-10 flex items-center justify-between border-t border-amber-200/70 bg-amber-100/40 flex-shrink-0">
        <span className="text-[11px] text-[#78350F]/80 italic truncate">© SCD Game · Usage pédagogique</span>
        {page.type !== 'cover' && (
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="px-3 py-1 rounded bg-amber-700 hover:bg-amber-600 disabled:bg-amber-400 disabled:cursor-not-allowed text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors flex-shrink-0"
            data-testid={`manual-download-${chapter.id}-${page.type}`}
          >
            <Download className="w-3.5 h-3.5" />
            {downloading ? 'Génération…' : 'PDF'}
          </button>
        )}
      </div>
    </div>
  );
});

export default ManualPage;

// ════════════════════════════════════════════════════════════════════
// Page-type renderers — ALL use the same heading + body type scale
// ════════════════════════════════════════════════════════════════════

function PageHeading({ children, chapterColor }) {
  return (
    <div className="mb-3 flex items-baseline gap-2 border-b-2 pb-2" style={{ borderColor: chapterColor }}>
      <span className="w-1.5 h-6 rounded-sm flex-shrink-0" style={{ background: chapterColor }} />
      <h2 className="text-xl font-bold text-[#0F172A] leading-tight" data-testid="manual-page-title">{children}</h2>
    </div>
  );
}

function CoverContent({ chapter, page }) {
  return (
    <div className="flex flex-col items-center justify-center text-center min-h-full space-y-4 py-2">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center text-white shadow-md flex-shrink-0"
        style={{ background: chapter.color }}
      >
        <BookMarked className="w-7 h-7" />
      </div>
      <h2 className="text-2xl font-bold text-[#0F172A] leading-tight" data-testid="manual-page-title">{page.title}</h2>
      <p className="text-sm italic text-[#334155]">{chapter.subtitle}</p>
      <p className="text-base text-[#0F172A] leading-relaxed px-2 font-normal">{page.intro}</p>
      <ul className="text-base text-[#0F172A] space-y-2 pt-2 self-start ml-2 list-disc list-inside font-normal">
        {(page.bullets || []).map((b) => (
          <li key={`bull-${b}`}>{b}</li>
        ))}
      </ul>
    </div>
  );
}

function DefinitionsContent({ page, chapter }) {
  return (
    <div>
      <PageHeading chapterColor={chapter.color}>{page.title}</PageHeading>
      <ol className="space-y-3">
        {page.items.map((it, i) => (
          <li key={`def-${it.term}`}>
            <div className="flex items-baseline gap-2">
              <span
                className="text-sm font-bold flex-shrink-0 w-5 text-center"
                style={{ color: chapter.color }}
              >
                {i + 1}.
              </span>
              <strong className="text-sm text-[#0F172A] font-bold">{it.term}</strong>
            </div>
            <p className="text-[13px] text-[#0F172A] leading-snug ml-7 mt-0.5 font-normal">{it.def}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function FormulasContent({ page, chapter }) {
  return (
    <div>
      <PageHeading chapterColor={chapter.color}>{page.title}</PageHeading>
      <div className="space-y-3">
        {page.items.map((it) => (
          <div
            key={`form-${it.name}`}
            className="bg-white rounded-md border-l-4 px-3.5 py-2.5 shadow-sm"
            style={{ borderLeftColor: chapter.color }}
          >
            <p className="text-sm font-bold text-[#0F172A]">{it.name}</p>
            <pre className="text-[12px] text-[#0F172A] font-mono mt-1.5 whitespace-pre-wrap leading-snug bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded">{it.formula}</pre>
            {it.example && (
              <p className="text-[12px] text-[#0F172A] mt-1.5 leading-snug">
                <strong className="font-bold">Exemple :</strong> <span className="font-normal">{it.example}</span>
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function VocabularyContent({ page, chapter }) {
  // Sort alphabetically so the apprenant can scan quickly.
  const sorted = [...page.items].sort((a, b) => a.word.localeCompare(b.word, 'fr'));
  return (
    <div>
      <PageHeading chapterColor={chapter.color}>{page.title}</PageHeading>
      <dl className="space-y-2.5">
        {sorted.map((it) => (
          <div key={`voc-${it.word}`} className="text-[13px] leading-snug">
            <dt className="font-bold text-[#0F172A] inline">{it.word}</dt>
            <dd className="inline text-[#0F172A] ml-1 font-normal">— {it.def}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Cas pratiques — same card structure as Formulas to keep the visual rhythm.
 * Each case has a situation + a corrigé. The corrigé is rendered in a softer
 * background to make the structure obvious at a glance.
 */
function CasesContent({ page, chapter }) {
  return (
    <div>
      <PageHeading chapterColor={chapter.color}>{page.title}</PageHeading>
      <div className="space-y-3">
        {page.items.map((it, i) => (
          <div
            key={`case-${it.title}`}
            className="bg-white rounded-md border-l-4 px-3.5 py-2.5 shadow-sm"
            style={{ borderLeftColor: chapter.color }}
          >
            <p className="text-sm font-bold text-[#0F172A] flex items-baseline gap-2">
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white flex-shrink-0"
                style={{ backgroundColor: chapter.color }}
              >
                Cas {i + 1}
              </span>
              <span className="leading-tight">{it.title}</span>
            </p>
            <p className="text-[12px] text-[#0F172A] mt-1.5 leading-snug font-normal">
              <strong className="font-bold">Situation : </strong>{it.situation}
            </p>
            <div className="text-[12px] text-[#0F172A] mt-1.5 leading-snug bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded">
              <strong className="font-bold">Corrigé : </strong>
              <span className="font-normal">{it.solution}</span>
            </div>
            {it.lesson && (
              <p className="text-[11px] italic text-[#334155] mt-1.5 leading-snug">
                <strong className="font-bold">Leçon : </strong>{it.lesson}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Pièges à éviter — red-accented cards listing classic mistakes + signal
 * in-game and corrective action.
 */
function PitfallsContent({ page, chapter }) {
  return (
    <div>
      <PageHeading chapterColor={chapter.color}>{page.title}</PageHeading>
      <div className="space-y-3">
        {page.items.map((it, i) => (
          <div
            key={`pit-${it.title}`}
            className="bg-white rounded-md border-l-4 border-rose-600 px-3.5 py-2.5 shadow-sm"
          >
            <p className="text-sm font-bold text-[#0F172A] flex items-baseline gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white flex-shrink-0 bg-rose-600">
                Piège {i + 1}
              </span>
              <span className="leading-tight">{it.title}</span>
            </p>
            <p className="text-[12px] text-[#0F172A] mt-1.5 leading-snug font-normal">
              <strong className="font-bold">L&apos;erreur : </strong>{it.mistake}
            </p>
            {it.signal && (
              <p className="text-[12px] text-[#0F172A] mt-1 leading-snug font-normal">
                <strong className="font-bold">Signal en jeu : </strong>{it.signal}
              </p>
            )}
            <div className="text-[12px] text-[#0F172A] mt-1.5 leading-snug bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded">
              <strong className="font-bold">Bonne pratique : </strong>
              <span className="font-normal">{it.fix}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Diagram page — a single inline SVG rendering with a short legend.
 */
function DiagramContent({ page, chapter }) {
  return (
    <div>
      <PageHeading chapterColor={chapter.color}>{page.title}</PageHeading>
      {page.intro && (
        <p className="text-sm text-[#0F172A] leading-relaxed mb-4 font-normal">{page.intro}</p>
      )}
      <div
        className="bg-white rounded-md border-l-4 px-4 py-4 shadow-sm flex justify-center"
        style={{ borderLeftColor: chapter.color }}
      >
        <DiagramSvg diagram={page.diagram} color={chapter.color} />
      </div>
      {page.legend && (
        <ul className="mt-4 space-y-1.5">
          {page.legend.map((it) => (
            <li key={`leg-${it.label}`} className="text-sm text-[#0F172A] flex items-start gap-2 leading-relaxed">
              <span
                className="w-2.5 h-2.5 rounded-sm mt-1.5 flex-shrink-0"
                style={{ backgroundColor: it.color || chapter.color }}
              />
              <span><strong className="font-bold">{it.label} : </strong><span className="font-normal">{it.desc}</span></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Inline SVG diagrams. Pure declarative — switch by id. Each diagram is
 * sized to fit inside the print area (max-width 360, max-height 320) so it
 * stays inside the page format on every screen / print.
 */
function DiagramSvg({ diagram, color }) {
  if (diagram === 'pyramid-statuts') {
    return (
      <svg viewBox="0 0 360 280" width="360" height="260" aria-label="Pyramide des statuts">
        <polygon points="180,10 290,80 270,80 195,30 165,30 90,80 70,80" fill={color} opacity="0.15" />
        <rect x="90" y="80" width="180" height="42" fill={color} opacity="0.85" />
        <text x="180" y="106" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700">SAS — CA &gt; 1 M €</text>
        <rect x="80" y="125" width="200" height="42" fill={color} opacity="0.65" />
        <text x="180" y="151" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700">SARL — 250k - 1M €</text>
        <rect x="65" y="170" width="230" height="42" fill={color} opacity="0.45" />
        <text x="180" y="196" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700">EURL — 92k - 250k €</text>
        <rect x="40" y="215" width="280" height="42" fill={color} opacity="0.25" />
        <text x="180" y="241" textAnchor="middle" fill="#0F172A" fontSize="13" fontWeight="700">Auto-entrepreneur — &lt; 92k €</text>
      </svg>
    );
  }
  if (diagram === 'tresorerie-cycle') {
    return (
      <svg viewBox="0 0 360 280" width="340" height="260" aria-label="Cycle de trésorerie">
        <circle cx="180" cy="140" r="100" fill="none" stroke={color} strokeWidth="2" strokeDasharray="6,3" />
        <g>
          <rect x="135" y="20" width="90" height="36" rx="4" fill={color} />
          <text x="180" y="42" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">Facture client</text>
        </g>
        <g>
          <rect x="265" y="125" width="90" height="36" rx="4" fill={color} opacity="0.75" />
          <text x="310" y="147" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">Encaissement</text>
        </g>
        <g>
          <rect x="135" y="232" width="90" height="36" rx="4" fill={color} opacity="0.55" />
          <text x="180" y="254" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">Paie chauffeurs</text>
        </g>
        <g>
          <rect x="5" y="125" width="90" height="36" rx="4" fill={color} opacity="0.35" />
          <text x="50" y="147" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">Carburant</text>
        </g>
        <text x="180" y="148" textAnchor="middle" fill="#0F172A" fontSize="14" fontWeight="700">BFR</text>
        <text x="180" y="166" textAnchor="middle" fill="#475569" fontSize="11">30-60 j</text>
      </svg>
    );
  }
  if (diagram === 'credit-amortissement') {
    return (
      <svg viewBox="0 0 360 240" width="360" height="220" aria-label="Amortissement d'un crédit">
        <line x1="40" y1="200" x2="340" y2="200" stroke="#0F172A" strokeWidth="1.5" />
        <line x1="40" y1="200" x2="40" y2="30" stroke="#0F172A" strokeWidth="1.5" />
        {Array.from({ length: 10 }).map((_, i) => {
          const x = 50 + i * 30;
          const interestHeight = 80 - i * 8;
          const principalHeight = 20 + i * 8;
          return (
            <g key={`bar-${i}`}>
              <rect x={x} y={200 - principalHeight} width="22" height={principalHeight} fill={color} opacity="0.9" />
              <rect x={x} y={200 - principalHeight - interestHeight} width="22" height={interestHeight} fill="#DC2626" opacity="0.7" />
            </g>
          );
        })}
        <text x="190" y="222" textAnchor="middle" fill="#0F172A" fontSize="11">Mois</text>
        <text x="20" y="120" textAnchor="middle" fill="#0F172A" fontSize="11" transform="rotate(-90 20 120)">Montant</text>
        <rect x="240" y="38" width="12" height="12" fill={color} />
        <text x="258" y="48" fill="#0F172A" fontSize="11">Capital</text>
        <rect x="240" y="56" width="12" height="12" fill="#DC2626" opacity="0.7" />
        <text x="258" y="66" fill="#0F172A" fontSize="11">Intérêts</text>
      </svg>
    );
  }
  if (diagram === 'satisfaction-curve') {
    return (
      <svg viewBox="0 0 360 220" width="340" height="200" aria-label="Courbe satisfaction → démission">
        <line x1="40" y1="180" x2="340" y2="180" stroke="#0F172A" strokeWidth="1.5" />
        <line x1="40" y1="180" x2="40" y2="20" stroke="#0F172A" strokeWidth="1.5" />
        <path d="M 40 30 L 100 35 L 160 50 L 220 95 L 270 140 L 320 170" fill="none" stroke={color} strokeWidth="2.5" />
        <rect x="40" y="160" width="170" height="20" fill="#10B981" opacity="0.15" />
        <text x="125" y="173" textAnchor="middle" fill="#10B981" fontSize="10" fontWeight="700">Productif</text>
        <rect x="210" y="160" width="60" height="20" fill="#F59E0B" opacity="0.2" />
        <text x="240" y="173" textAnchor="middle" fill="#B45309" fontSize="10" fontWeight="700">Alerte</text>
        <rect x="270" y="160" width="70" height="20" fill="#DC2626" opacity="0.2" />
        <text x="305" y="173" textAnchor="middle" fill="#DC2626" fontSize="10" fontWeight="700">Démission</text>
        <text x="40" y="200" textAnchor="middle" fill="#475569" fontSize="10">100</text>
        <text x="210" y="200" textAnchor="middle" fill="#475569" fontSize="10">40</text>
        <text x="340" y="200" textAnchor="middle" fill="#475569" fontSize="10">0</text>
        <text x="190" y="218" textAnchor="middle" fill="#0F172A" fontSize="11">Satisfaction (100 → 0)</text>
      </svg>
    );
  }
  if (diagram === 'flotte-comparatif') {
    return (
      <svg viewBox="0 0 360 240" width="340" height="220" aria-label="Achat vs leasing">
        <line x1="40" y1="200" x2="340" y2="200" stroke="#0F172A" strokeWidth="1.5" />
        <line x1="40" y1="200" x2="40" y2="20" stroke="#0F172A" strokeWidth="1.5" />
        <rect x="60" y="60" width="50" height="140" fill={color} opacity="0.85" />
        <text x="85" y="55" textAnchor="middle" fill="#0F172A" fontSize="12" fontWeight="700">Achat</text>
        <text x="85" y="220" textAnchor="middle" fill="#0F172A" fontSize="10">35 k€</text>
        <rect x="160" y="40" width="50" height="160" fill="#DC2626" opacity="0.7" />
        <text x="185" y="35" textAnchor="middle" fill="#0F172A" fontSize="12" fontWeight="700">Leasing</text>
        <text x="185" y="220" textAnchor="middle" fill="#0F172A" fontSize="10">39,6 k€</text>
        <rect x="260" y="100" width="50" height="100" fill="#10B981" opacity="0.7" />
        <text x="285" y="95" textAnchor="middle" fill="#0F172A" fontSize="12" fontWeight="700">Crédit</text>
        <text x="285" y="220" textAnchor="middle" fill="#0F172A" fontSize="10">37 k€</text>
        <text x="180" y="20" textAnchor="middle" fill="#0F172A" fontSize="11" fontWeight="700">Coût total 3 ans (camion 60k €)</text>
      </svg>
    );
  }
  if (diagram === 'ma-funnel') {
    return (
      <svg viewBox="0 0 360 280" width="360" height="260" aria-label="Funnel M&A">
        {['Cibles', 'NDA', 'Due dil.', 'LOI', 'SPA', 'Closing'].map((label, i) => {
          const y = 20 + i * 38;
          const width = 280 - i * 30;
          const x = (360 - width) / 2;
          const opacity = 0.95 - i * 0.13;
          return (
            <g key={`fn-${label}`}>
              <rect x={x} y={y} width={width} height={28} fill={color} opacity={opacity} />
              <text x={180} y={y + 19} textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700">{label}</text>
            </g>
          );
        })}
      </svg>
    );
  }
  if (diagram === 'client-tiers') {
    const tiers = [
      { id: 'mefiant', label: 'Méfiant', color: '#DC2626', min: 0, max: 20 },
      { id: 'neutre', label: 'Neutre', color: '#9CA3AF', min: 20, max: 40 },
      { id: 'confiant', label: 'Confiant', color: '#0891B2', min: 40, max: 65 },
      { id: 'fidele', label: 'Fidèle', color: '#D97706', min: 65, max: 85 },
      { id: 'partenaire', label: 'Partenaire', color: '#10B981', min: 85, max: 100 },
    ];
    return (
      <svg viewBox="0 0 360 200" width="340" height="180" aria-label="Paliers clients">
        <rect x="20" y="60" width="320" height="40" fill="#F3F4F6" rx="4" />
        {tiers.map(t => {
          const x = 20 + (t.min / 100) * 320;
          const w = ((t.max - t.min) / 100) * 320;
          return (
            <g key={t.id}>
              <rect x={x} y={60} width={w} height={40} fill={t.color} opacity="0.8" />
              <text x={x + w / 2} y={84} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="700">{t.label}</text>
            </g>
          );
        })}
        <text x="20"  y="125" fill="#475569" fontSize="11">0</text>
        <text x="84"  y="125" fill="#475569" fontSize="11">20</text>
        <text x="148" y="125" fill="#475569" fontSize="11">40</text>
        <text x="228" y="125" fill="#475569" fontSize="11">65</text>
        <text x="292" y="125" fill="#475569" fontSize="11">85</text>
        <text x="328" y="125" fill="#475569" fontSize="11">100</text>
        <text x="180" y="150" textAnchor="middle" fill="#0F172A" fontSize="12" fontWeight="700">Score de fidélité (0 → 100)</text>
        <text x="180" y="168" textAnchor="middle" fill="#475569" fontSize="10">+2 ponctuel · -15 annulation · -4 retard · +10 année sans incident</text>
      </svg>
    );
  }
  if (diagram === 'crisis-timeline') {
    return (
      <svg viewBox="0 0 360 220" width="340" height="200" aria-label="Chronologie crise">
        <line x1="20" y1="100" x2="340" y2="100" stroke="#0F172A" strokeWidth="2" />
        {[
          { x: 50,  label: 'Signal',  desc: 'Indicateurs',          c: '#F59E0B' },
          { x: 130, label: 'Alerte',  desc: 'Tréso < 0',            c: '#FB7185' },
          { x: 210, label: 'Crise',   desc: 'Streak > 14 j',        c: '#DC2626' },
          { x: 290, label: 'Liq.',    desc: 'Tribunal',             c: '#7F1D1D' },
        ].map(s => (
          <g key={`step-${s.label}`}>
            <circle cx={s.x} cy="100" r="12" fill={s.c} />
            <text x={s.x} y="104" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="700">!</text>
            <text x={s.x} y="75"  textAnchor="middle" fill="#0F172A" fontSize="12" fontWeight="700">{s.label}</text>
            <text x={s.x} y="130" textAnchor="middle" fill="#475569" fontSize="10">{s.desc}</text>
          </g>
        ))}
        <text x="180" y="180" textAnchor="middle" fill="#0F172A" fontSize="11" fontWeight="700">30 jours max entre alerte et liquidation</text>
      </svg>
    );
  }
  if (diagram === 'incoterm-bands') {
    // Horizontal responsibility bands. Each row = an Incoterm. Each column
    // = a leg of the journey. Green = paid/insured by seller, orange =
    // buyer's responsibility. The pivot column where the band switches
    // colour is the "risk transfer point".
    const legs = ['Usine vendeur', 'Transport pré-acheminement', 'Transport principal', 'Transport post-livraison', 'Douane export', 'Douane import'];
    // For each Incoterm, which legs are paid by the seller (true = green).
    const terms = [
      { id: 'EXW', pays: [true, false, false, false, false, false] },
      { id: 'FCA', pays: [true, true,  false, false, true,  false] },
      { id: 'CPT', pays: [true, true,  true,  false, true,  false] },
      { id: 'DAP', pays: [true, true,  true,  true,  true,  false] },
      { id: 'DDP', pays: [true, true,  true,  true,  true,  true ] },
    ];
    const colW = 50;
    const rowH = 30;
    const x0 = 60;
    const y0 = 50;
    const width = x0 + colW * legs.length + 20;
    const height = y0 + rowH * terms.length + 70;
    return (
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} aria-label="Incoterms 2020 — répartition des responsabilités">
        <text x={width / 2} y="22" textAnchor="middle" fill="#0F172A" fontSize="13" fontWeight="700">
          Incoterms 2020 — qui paie chaque étape ?
        </text>
        {/* Column headers */}
        {legs.map((leg, i) => (
          <g key={`leg-${leg}`}>
            <line x1={x0 + i * colW} y1={y0 - 4} x2={x0 + i * colW} y2={y0 + rowH * terms.length + 4} stroke="#E2E8F0" strokeWidth="0.5" />
            <text x={x0 + i * colW + colW / 2} y={y0 - 6} textAnchor="middle" fill="#475569" fontSize="8.5">
              {leg.split(' ').slice(0, 2).join(' ')}
            </text>
            {leg.split(' ').length > 2 && (
              <text x={x0 + i * colW + colW / 2} y={y0 + 2} textAnchor="middle" fill="#475569" fontSize="8.5">
                {leg.split(' ').slice(2).join(' ')}
              </text>
            )}
          </g>
        ))}
        {/* Rows */}
        {terms.map((t, ri) => (
          <g key={`row-${t.id}`}>
            <text x={x0 - 8} y={y0 + ri * rowH + rowH / 2 + 4} textAnchor="end" fill={color} fontSize="11" fontWeight="700">{t.id}</text>
            {t.pays.map((paid, ci) => (
              <rect
                key={`cell-${t.id}-${ci}`}
                x={x0 + ci * colW}
                y={y0 + ri * rowH + 4}
                width={colW - 2}
                height={rowH - 6}
                fill={paid ? '#10B981' : '#F97316'}
                opacity={0.78}
                rx={3}
              />
            ))}
          </g>
        ))}
        {/* Legend */}
        <rect x={x0} y={y0 + rowH * terms.length + 18} width="14" height="14" fill="#10B981" opacity="0.78" rx="2" />
        <text x={x0 + 20} y={y0 + rowH * terms.length + 30} fill="#0F172A" fontSize="10">Vendeur paie / supporte le risque</text>
        <rect x={x0 + 175} y={y0 + rowH * terms.length + 18} width="14" height="14" fill="#F97316" opacity="0.78" rx="2" />
        <text x={x0 + 195} y={y0 + rowH * terms.length + 30} fill="#0F172A" fontSize="10">Acheteur paie / supporte le risque</text>
        <text x={x0} y={y0 + rowH * terms.length + 50} fill="#64748B" fontSize="9.5">
          La frontière verte → orange indique le « point de bascule » du risque, point clé à inscrire dans le contrat.
        </text>
      </svg>
    );
  }
  // Fallback — small ID badge so dev sees the missing diagram clearly.
  return (
    <div className="text-xs italic text-[#64748B] px-4 py-2">
      Diagramme indisponible : {diagram}
    </div>
  );
}
